import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { databaseApi } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { DatabaseDetails } from "@/types/database";
import {
  AlertTriangle,
  Clock,
  Filter,
  Search,
  Trash2,
  XCircle
} from "lucide-react";
import React, { useEffect, useState } from "react";

interface AuditTabProps {
  isActive: boolean;
}

const AuditTab: React.FC<AuditTabProps> = ({ isActive }) => {
  const [deletedDatabases, setDeletedDatabases] = useState<DatabaseDetails[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterSize, setFilterSize] = useState("all");

  useEffect(() => {
    if (isActive) {
      loadDeletedDatabases();
    }
  }, [isActive]);

  const loadDeletedDatabases = async () => {
    setIsLoading(true);
    try {
      const response = await databaseApi.getDeletedDatabases();
      if (response.success && response.data) {
        setDeletedDatabases(response.data.databases);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const getDatabaseIcon = (dbtype: string) => {
    switch (dbtype) {
      case "MYSQL":
        return "🐬";
      case "POSTGRES":
        return "🐘";
      case "MONGODB":
        return "🍃";
      default:
        return "🗄️";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "deleted":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "deleted":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Filter and search logic
  const filteredDatabases = deletedDatabases.filter((database) => {
    const matchesSearch =
      database.dbname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      database.notes?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === "all" || database.dbtype === filterType;
    const matchesSize = filterSize === "all" || database.size === filterSize;

    return matchesSearch && matchesType && matchesSize;
  });

  if (!isActive) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Audit Trail</h2>
          <p className="text-muted-foreground">
            View deleted databases and audit information
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadDeletedDatabases}
          disabled={isLoading}
        >
          <Clock className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Database Type Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="MYSQL">MySQL</SelectItem>
                <SelectItem value="POSTGRES">PostgreSQL</SelectItem>
                <SelectItem value="MONGODB">MongoDB</SelectItem>
              </SelectContent>
            </Select>

            {/* Size Filter */}
            <Select value={filterSize} onValueChange={setFilterSize}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sizes</SelectItem>
                <SelectItem value="1GB">1GB</SelectItem>
                <SelectItem value="5GB">5GB</SelectItem>
                <SelectItem value="10GB">10GB</SelectItem>
                <SelectItem value="50GB">50GB</SelectItem>
                <SelectItem value="100GB">100GB</SelectItem>
              </SelectContent>
            </Select>

            {/* Clear Filters */}
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
                setFilterType("all");
                setFilterSize("all");
              }}
            >
              <Filter className="h-4 w-4 mr-1" />
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {filteredDatabases.length} of {deletedDatabases.length}{" "}
          deleted databases
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2">Loading deleted databases...</span>
        </div>
      )}

      {/* No Results */}
      {!isLoading && filteredDatabases.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Trash2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No deleted databases found
            </h3>
            <p className="text-muted-foreground text-center">
              {deletedDatabases.length === 0
                ? "No databases have been deleted yet."
                : "No databases match your current filters."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Deleted Databases List */}
      {!isLoading && filteredDatabases.length > 0 && (
        <div className="space-y-4">
          {filteredDatabases.map((database) => (
            <Card key={database.id} className="border-red-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {getDatabaseIcon(database.dbtype)}
                    </span>
                    <div>
                      <CardTitle className="text-lg text-red-800">
                        {database.dbname}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <span className="capitalize">{database.dbtype}</span>
                        <span>•</span>
                        <span>{database.size}</span>
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                        database.status
                      )}`}
                    >
                      <div className="flex items-center gap-1">
                        {getStatusIcon(database.status)}
                        <span className="capitalize">Deleted</span>
                      </div>
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">
                      Timeline
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div>Created: {formatDateTime(database.created_at)}</div>
                      <div>
                        Deleted:{" "}
                        {database.deleted_at
                          ? formatDateTime(database.deleted_at)
                          : "Unknown"}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">
                      Notes
                    </h4>
                    <div className="text-sm text-red-600">
                      {database.notes || "No additional notes"}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="text-xs text-muted-foreground">
                    <div>Created by: {database.created_by || "system"}</div>
                    {database.scheduled_deletion_date && (
                      <div>
                        Scheduled for deletion:{" "}
                        {formatDateTime(database.scheduled_deletion_date)}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AuditTab;
